import React, { useEffect, useState } from 'react'

import { Dialog3 as Dialog } from '@st-operations/uikit/src/v1/index.js'
import {
  Button,
  BUTTON_THEME,
  CheckBox,
  IconButton,
  Select,
} from '@st-operations/uikit/src/v2/index.js'

import { useDestinations, useRegions } from '_hooks'

import countryToRegionMap from './countryToRegionMap.js'
import NameInput, { transformName } from '../name-input'

import './styles.scss'

const ModalCloneAudience = ({
  selectedAudiences,
  isModalCloneOpen,
  clone,
  closeModal,
  resourceOptimized,
}) => {
  const actionLabel = selectedAudiences.length > 1 ? 'Clone Audiences' : 'Clone Audience'
  const [audiences, setAudiences] = useState({})
  const [isApplyAll, setIsApplyAll] = useState(false)

  const regions = useRegions()
  const destinations = useDestinations()

  useEffect(() => {
    setAudiences(
      selectedAudiences.reduce(
        (acc, audience) =>
          resourceOptimized && !audience.taxonomy?.distribution
            ? acc // don't include it if it doesn't have distribution
            : {
                ...acc,
                [audience._id]: transformName.in(audience),
              },
        {}
      )
    )
  }, [selectedAudiences])

  useEffect(() => {
    if (isModalCloneOpen && selectedAudiences.length === 0) {
      closeModal()
      toast.error('No audience selected.')
    }
  }, [isModalCloneOpen && selectedAudiences])

  const handleChange = (id) => (value, name) => {
    if (isApplyAll) {
      setAudiences((prev) => {
        const newAudiences = Object.fromEntries(
          Object.entries(prev).map(([audienceId, audience]) => [
            audienceId,
            { ...audience, [name]: value },
          ])
        )
        return newAudiences
      })
    } else {
      setAudiences((prev) => ({
        ...prev,
        [id]: { ...prev[id], [name]: value },
      }))
    }
  }

  const handleConfirm = () => {
    const newAudiences = Object.fromEntries(
      Object.entries(audiences).map(([key, audience]) => [key, transformName.out(audience)])
    )
    clone(newAudiences)
    closeModal()
  }

  const handleNameInput = (id) => (nameIndex) => (event) => {
    const { value } = event.target

    if (isApplyAll) {
      setAudiences((prev) => {
        const newAudiences = Object.fromEntries(
          Object.entries(prev).map(([audienceId, audience]) => [
            audienceId,
            {
              ...audience,
              name: audience.name.map((part, index) => (index === nameIndex ? value : part)),
            },
          ])
        )
        return newAudiences
      })
    } else {
      setAudiences((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          name: prev[id].name.map((part, index) => (index === nameIndex ? value : part)),
        },
      }))
    }
  }

  const handleAddNameInput = (id) => (index) => () => {
    if (isApplyAll) {
      setAudiences((prev) => {
        const newAudiences = Object.fromEntries(
          Object.entries(prev).map(([audienceId, audience]) => [
            audienceId,
            {
              ...audience,
              name: [...audience.name.slice(0, index), '', ...audience.name.slice(index)],
            },
          ])
        )
        return newAudiences
      })
    } else {
      setAudiences((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          name: [...prev[id].name.slice(0, index), '', ...prev[id].name.slice(index)],
        },
      }))
    }
  }

  const getDistributionLabel = (distribution) => {
    return `${distribution.destination} / ${distribution.geo.join(', ')}`
  }

  return (
    <Dialog open={isModalCloneOpen} className="modal-clone-audience">
      <header className="modal-clone-audience__header">
        <h2>{actionLabel}</h2>
        <div
          className="modal-bulk-edit-audience__apply-to-all"
          title="Changes made in one audience will overwrite the others"
        >
          <CheckBox checked={isApplyAll} onChange={() => setIsApplyAll((prev) => !prev)} />
          Apply changes to all audiences
        </div>
        <IconButton
          onClick={closeModal}
          aria-label="Close modal"
          iconProps={{
            icon: 'delete',
            type: 'fill',
            width: 16,
            height: 16,
            color: '#3d3d3d',
          }}
        />
      </header>
      <section className="modal-clone-audience__audiences-list">
        <For each="audience" of={Object.values(audiences)}>
          <div key={audience._id} className="modal-clone-audience__audiences-item">
            <h4 className="modal-clone-audience__name">{transformName.out(audience).name}</h4>
            <NameInput
              label="Audience Name"
              value={audience.name}
              onChange={handleNameInput(audience._id)}
              addInput={handleAddNameInput(audience._id)}
            />
            <If condition={resourceOptimized}>
              <Select
                name="distributions"
                label="Distributions"
                className="audience-creator__distributions-field"
                isSearchable
                useReactSelect
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                options={audience?.taxonomy?.distribution?.map((distribution) => ({
                  label: getDistributionLabel(distribution),
                  value: distribution,
                }))}
                value={
                  audience?.distribution
                    ? {
                        value: audience.distribution,
                        label: getDistributionLabel(audience.distribution),
                      }
                    : undefined
                }
                onChange={({ value: newDistribution }) => {
                  //map distribution geos to audience regions
                  const newRegions = newDistribution.geo.map(
                    (geo) => countryToRegionMap[geo === 'GLOBAL' ? 'Global' : geo]
                  )

                  //map distribution destination to audience destinations
                  const newDestination = destinations.find(
                    ({ slug }) => slug === newDistribution.destination
                  )

                  const mappingId = newDistribution.mapping_ids

                  handleChange(audience._id)(newDistribution, 'distribution')
                  handleChange(audience._id)(audience.ids[mappingId], 'resource_optimized_clone')
                  handleChange(audience._id)(newRegions, 'regions')
                  handleChange(audience._id)([newDestination], 'destinations')
                }}
              />
            </If>
            <If condition={!resourceOptimized}>
              <Select
                name="regions"
                label="Regions"
                className="audience-creator__regions-field"
                isMulti
                isSearchable
                useReactSelect
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                options={regions?.filter((region) => {
                  return !audience?.regions?.some(
                    (audienceRegion) => audienceRegion.name === region.name
                  )
                })}
                value={audience?.regions?.sort((a, b) => (a.name > b.name ? 1 : -1))}
                onChange={(newRegions) => {
                  handleChange(audience._id)(newRegions || [], 'regions')
                }}
              />
              <Select
                name="destinations"
                label="Destinations"
                isMulti
                isSearchable
                useReactSelect
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                options={
                  resourceOptimized
                    ? destinations.filter((destination) => {
                        const audienceDestinations = audience?.taxonomy?.distribution?.map(
                          ({ destination }) => destination
                        )
                        return audienceDestinations.includes(destination.slug)
                      })
                    : destinations.filter((destination) => {
                        return !audience.destinations?.some(
                          (audienceDestination) => audienceDestination.name === destination.name
                        )
                      })
                }
                value={audience.destinations
                  ?.sort((a, b) => (a.name > b.name ? 1 : -1))
                  .map((destination) => ({
                    ...destination,
                    label: destination.name,
                  }))}
                onChange={(destinations) => {
                  handleChange(audience._id)(destinations || [], 'destinations')
                }}
              />
            </If>
          </div>
        </For>
      </section>
      <footer className="modal-clone-audience__footer">
        <div className="modal-clone-audience__actions">
          <Button onClick={closeModal} theme={BUTTON_THEME.SECONDARY}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>{actionLabel}</Button>
        </div>
      </footer>
    </Dialog>
  )
}

export default ModalCloneAudience
